import React, { useEffect, useState } from 'react';
import { BookOpen, Search, Loader2, Plus, FileText, Eye, Clock, ExternalLink, Beaker, CheckCircle2, Sparkles } from 'lucide-react';
import PoPShell from './PoPShell';
import { usePoPAuth } from '../../context/PoPAuthContext';
import { popListContent, popCreateContent, popEnroll, popMyEnrollments, roleLabel } from '../../lib/popApi';

const TYPE_META = {
  course:      { label: 'Course',      Icon: BookOpen, color: 'text-emerald-300', bg: 'bg-emerald-500/10' },
  research:    { label: 'Research',    Icon: Beaker,   color: 'text-violet-300',  bg: 'bg-violet-500/10' },
  case_study:  { label: 'Case Study',  Icon: FileText, color: 'text-amber-300',   bg: 'bg-amber-500/10' },
};

const ContentCard = ({ c, enrolled, onEnroll, user }) => {
  const meta = TYPE_META[c.type] || TYPE_META.course;
  const Icon = meta.Icon;
  const [busy, setBusy] = useState(false);
  const [localEnrolled, setLocalEnrolled] = useState(enrolled);
  const doEnroll = async () => {
    setBusy(true);
    try {
      await onEnroll(c.id);
      setLocalEnrolled(true);
    } catch (_e) { /* noop */ }
    setBusy(false);
  };
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/40 p-5 flex flex-col" data-testid={`content-card-${c.id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${meta.bg} ${meta.color} font-bold`}>
          <Icon className="w-3 h-3" /> {meta.label}
        </span>
        {c.duration_minutes ? (
          <span className="text-[10px] text-white/50 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {c.duration_minutes} min</span>
        ) : null}
      </div>
      <div className="text-base font-serif font-semibold text-white mb-1">{c.title}</div>
      <div className="text-[11px] text-white/50 mb-2">
        by <span className="text-white/80 font-medium">{c.author_name}</span> · {roleLabel(c.author_role)}
      </div>
      <p className="text-xs text-white/70 leading-relaxed line-clamp-3 mb-3 flex-1">{c.description}</p>
      {c.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {c.tags.slice(0, 4).map((t) => (
            <span key={t} className="text-[9px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/70">{t}</span>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {c.content_url ? (
            <a href={c.content_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 border border-white/10 hover:bg-white/10 text-white/80 text-xs font-semibold"
              data-testid={`content-open-${c.id}`}>
              Open <ExternalLink className="w-3 h-3" />
            </a>
          ) : <span className="text-[10px] text-white/40 italic">Coming soon</span>}
          {user && (
            <button onClick={doEnroll} disabled={busy || localEnrolled}
              data-testid={`content-enroll-${c.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold ${
                localEnrolled
                  ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 cursor-default'
                  : 'bg-amber-300 hover:bg-amber-200 text-slate-900'
              }`}>
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> :
                localEnrolled ? <><CheckCircle2 className="w-3 h-3" /> Enrolled</> :
                <><Sparkles className="w-3 h-3" /> Enroll</>}
            </button>
          )}
        </div>
        <span className="text-[10px] text-white/40 inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {c.views || 0}</span>
      </div>
    </div>
  );
};

const CreateContentForm = ({ defaultType, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    type: defaultType, title: '', description: '', category: '', tags_csv: '',
    content_url: '', duration_minutes: '', thumbnail_url: '',
  });
  useEffect(() => { setForm((f) => ({ ...f, type: defaultType })); }, [defaultType]);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const payload = {
        ...form,
        tags: form.tags_csv.split(',').map((s) => s.trim()).filter(Boolean),
        duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes, 10) : undefined,
      };
      delete payload.tags_csv;
      await popCreateContent(payload);
      setOpen(false); setForm({ ...form, title: '', description: '', tags_csv: '', content_url: '' });
      onCreated();
    } catch (e) { setErr((e && e.response && e.response.data && e.response.data.detail) || 'Could not publish'); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-6">
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid="content-create-open"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-amber-300 hover:bg-amber-200 text-slate-900 text-sm font-semibold">
          <Plus className="w-4 h-4" /> Publish {TYPE_META[defaultType].label.toLowerCase()}
        </button>
      ) : (
        <div className="rounded-xl border border-white/10 bg-slate-900/60 p-5 grid sm:grid-cols-2 gap-3">
          <input placeholder="Title" value={form.title} onChange={upd('title')} data-testid="content-form-title"
            className="col-span-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <textarea placeholder="Description" value={form.description} onChange={upd('description')} data-testid="content-form-desc" rows={3}
            className="col-span-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <input placeholder="Category (e.g. Enterprise AI)" value={form.category} onChange={upd('category')}
            className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <input placeholder="Tags (comma-separated)" value={form.tags_csv} onChange={upd('tags_csv')}
            className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <input placeholder="Content URL (video/deck/PDF)" value={form.content_url} onChange={upd('content_url')} data-testid="content-form-url"
            className="col-span-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <input type="number" placeholder="Duration (min, optional)" value={form.duration_minutes} onChange={upd('duration_minutes')}
            className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <input placeholder="Thumbnail URL (optional)" value={form.thumbnail_url} onChange={upd('thumbnail_url')}
            className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          {err && <div className="text-xs text-rose-300 col-span-2">{err}</div>}
          <div className="col-span-2 flex gap-2">
            <button onClick={submit} disabled={busy} data-testid="content-form-submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-amber-300 hover:bg-amber-200 disabled:opacity-50 text-slate-900 text-sm font-semibold">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Publish
            </button>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-md bg-white/5 hover:bg-white/10 text-sm text-white/70">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

const PoPLibrary = () => {
  const { user } = usePoPAuth();
  const [tab, setTab] = useState('course');
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [enrolledIds, setEnrolledIds] = useState(new Set());

  const load = async () => {
    setBusy(true);
    try {
      const params = { type: tab };
      if (q) params.q = q;
      const r = await popListContent(params);
      setItems(r.results || []);
      if (user) {
        try {
          const me = await popMyEnrollments();
          setEnrolledIds(new Set((me.results || []).filter((e) => e.subject_type === 'content').map((e) => e.subject_id)));
        } catch (_e) { /* noop */ }
      }
    } catch (_e) { setItems([]); } finally { setBusy(false); }
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [tab, q, user]);

  const doEnroll = async (contentId) => {
    await popEnroll('content', contentId);
    setEnrolledIds((prev) => new Set([...prev, contentId]));
  };

  const canPublish = user && !['unassigned', 'student'].includes(user.role);

  return (
    <PoPShell activeTab="library">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">KNOWLEDGE LIBRARY</div>
        <h1 className="font-serif text-3xl sm:text-4xl">Courses, research & case studies.</h1>
        <p className="text-sm text-white/60 max-w-2xl mt-1">Published by verified members of the ecosystem. Role-gated access where required.</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(TYPE_META).map(([id, m]) => (
          <button key={id} onClick={() => setTab(id)}
            data-testid={`library-tab-${id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition ${
              tab === id ? 'bg-amber-300 text-slate-900 border-amber-300' : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
            }`}>
            <m.Icon className="w-3.5 h-3.5" /> {m.label}
          </button>
        ))}
        <div className="relative flex-1 min-w-[240px] ml-auto">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="library-search"
            placeholder="Search title, description, tags…"
            className="w-full pl-9 pr-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-300" />
        </div>
      </div>

      {canPublish && <CreateContentForm defaultType={tab} onCreated={load} />}

      {busy ? (
        <div className="flex items-center justify-center py-24 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading library…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/15 bg-slate-900/40 p-10 text-center">
          {(() => { const M = TYPE_META[tab]; const I = M.Icon; return <I className="w-8 h-8 text-white/30 mx-auto mb-2" />; })()}
          <div className="text-sm text-white/60">No {TYPE_META[tab].label.toLowerCase()}s published yet.</div>
          {canPublish && <div className="text-[11px] text-white/40 mt-1">Be the first to publish.</div>}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => <ContentCard key={c.id} c={c} enrolled={enrolledIds.has(c.id)} onEnroll={doEnroll} user={user} />)}
        </div>
      )}
    </PoPShell>
  );
};

export default PoPLibrary;
